
import { BlockPeriod, IGenericError } from '@libs/entity-lib';
import { createEntityAdapter, EntityAdapter, EntityState, Update } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as fromCustomer from './block-period.actions';

export interface State extends EntityState<BlockPeriod> {
    loading: boolean;
    error: IGenericError | null;
    // Histórico paginado (aba "Histórico"). Mantido em paralelo ao
    // EntityAdapter (que guarda apenas os futuros/em andamento).
    historyContent: BlockPeriod[];
    historyLoading: boolean;
    historyPage: number;
    historyTotalPages: number;
    historyTotalElements: number;
    historyHasMore: boolean;
}

const adapter: EntityAdapter<BlockPeriod> = createEntityAdapter<BlockPeriod>({
  selectId: (blockPeriod: BlockPeriod) => blockPeriod.id
});

export const initialState: State = adapter.getInitialState({
  loading: false,
  error: null,
  historyContent: [],
  historyLoading: false,
  historyPage: 0,
  historyTotalPages: 0,
  historyTotalElements: 0,
  historyHasMore: true,
});


export const reducer = createReducer(
  initialState,
  on(fromCustomer.fetchBlockPeriod, (state) =>  ({ ...state, loading: true, error: null })),
  on(fromCustomer.fetchBlockPeriodSuccessfuly, (state, { blockPeriodList }) => {
    return adapter.setAll(blockPeriodList, {
      ...state,
      loading: false,
      error: null
    })
  }),
  on(fromCustomer.fetchBlockPeriodFailed, (state, { error }) =>  adapter.setAll([], { ...state, loading: false, error })),

  // Add
  on(fromCustomer.addBlockPeriod, (state) =>  ({ ...state, loading: true, error: null })),
  on(fromCustomer.addBlockPeriodSuccessfuly, (state, { blockPeriod }) => {
    return adapter.addOne(blockPeriod, {
      ...state,
      loading: false,
      error: null
    })
  }),
  on(fromCustomer.addBlockPeriodFailed, (state, { error }) =>  adapter.setAll([], { ...state, loading: false, error })),

  // Add em lote (um bloqueio por dia selecionado). O effect refaz o fetch, então
  // aqui basta soltar o loading — nada de setAll([]) no erro, que apagaria a
  // lista inteira por causa de um dia que falhou.
  on(fromCustomer.addBlockPeriodBatch, (state) => ({ ...state, loading: true, error: null })),
  on(fromCustomer.addBlockPeriodBatchSuccessfuly, (state) => ({ ...state, loading: false, error: null })),
  on(fromCustomer.addBlockPeriodBatchPartial, (state) => ({ ...state, loading: false, error: null })),
  on(fromCustomer.addBlockPeriodBatchFailed, (state, { error }) => ({ ...state, loading: false, error })),

// Update
on(fromCustomer.updateBlockPeriod, (state) =>  ({ ...state, loading: true, error: null })),
on(fromCustomer.updateBlockPeriodSuccessfuly, (state, { blockPeriod }) => {
  const update: Update<BlockPeriod> = {
    id: blockPeriod.id, 
    changes: { ...blockPeriod }
  };

  return adapter.updateOne(update, {
    ...state,
    loading: false,
    error: null
  });
}),
on(fromCustomer.updateBlockPeriodFailed, (state, { error }) =>  adapter.setAll([], { ...state, loading: false, error })),


  // Remove
  on(fromCustomer.removeBlockPeriod, (state) =>  ({ ...state, loading: true, error: null })),
  on(fromCustomer.removeBlockPeriodSuccessfuly, (state, { id }) => {
    return adapter.removeOne(id, {
      ...state,
      loading: false,
      error: null
    })
  }),
  on(fromCustomer.removeBlockPeriodFailed, (state, { error }) =>  adapter.setAll([], { ...state, loading: false, error })),

  // Histórico paginado
  on(fromCustomer.fetchBlockPeriodHistory, (state, { reset }) => ({
    ...state,
    historyLoading: true,
    historyContent: reset ? [] : state.historyContent,
    historyPage: reset ? 0 : state.historyPage,
  })),
  on(fromCustomer.fetchBlockPeriodHistorySuccessfuly, (state, { response, reset }) => {
    const incoming = response?.content ?? [];
    const merged = reset ? incoming : [...state.historyContent, ...incoming];
    return {
      ...state,
      historyLoading: false,
      historyContent: merged,
      historyPage: response?.number ?? state.historyPage,
      historyTotalPages: response?.totalPages ?? 0,
      historyTotalElements: response?.totalElements ?? 0,
      historyHasMore: !(response?.last ?? true),
    };
  }),
  on(fromCustomer.fetchBlockPeriodHistoryFailed, (state, { error }) => ({
    ...state,
    historyLoading: false,
    error,
  })),
);

export const { selectAll } = adapter.getSelectors();